import { parseRequiredHashtags } from '@/lib/parseRequiredHashtags';
import { safeCaptionTokens } from './safeCaptionTokens';

export type SubmissionFeedbackInput = {
  disqualified?: boolean | null;
  disqualification_reason?: string | null;
  approval_reason?: string | null;
  admin_approval?: string | null;
  suspected_duplicate?: boolean | null;
  rules_check_failures?: string[] | null;
  platform?: string | null;
  rules_check_missing_hashtags?: string[] | null;
  rules_check_missing_mentions?: string[] | null;
};

export type SubmissionFeedbackCampaign = {
  required_hashtags?: string | null;
  required_mention?: string | null;
};

/** `missing`: what the automatic check did not find. `required`: what the campaign asks for on this platform. */
export type CaptionTokens = { source: 'missing' | 'required'; tokens: string[] };

export type SubmissionFeedback =
  | { kind: 'none' }
  | { kind: 'disqualified'; reasonCode: string | null }
  | { kind: 'approval'; reasonCode: string; muted: boolean; captionTokens?: CaptionTokens }
  | { kind: 'underReview' }
  | { kind: 'rulesCheckFailures'; codes: string[]; captionTokens?: CaptionTokens };

const CAPTION_CODES = new Set(['missing_hashtag', 'missing_mention']);

function requiredCaptionTokens(platform: string | null | undefined, campaign?: SubmissionFeedbackCampaign): string[] {
  if (!platform || !campaign) return [];
  const byPlatform: Record<string, string | undefined> = parseRequiredHashtags(campaign.required_hashtags, true);
  const words = `${byPlatform[platform] ?? ''} ${campaign.required_mention ?? ''}`.split(/[\s,]+/);
  return [...safeCaptionTokens(words, '#'), ...safeCaptionTokens(words, '@')];
}

/**
 * Names the tags behind a caption failure: the ones the check recorded as
 * missing, or, for checks recorded before the backend kept them (and reviewer
 * rejections without a check), the ones the campaign requires on this platform.
 */
function captionTokensFor(s: SubmissionFeedbackInput, campaign?: SubmissionFeedbackCampaign): CaptionTokens | null {
  const missing = [
    ...safeCaptionTokens(s.rules_check_missing_hashtags, '#'),
    ...safeCaptionTokens(s.rules_check_missing_mentions, '@'),
  ];
  if (missing.length > 0) return { source: 'missing', tokens: missing };
  const required = requiredCaptionTokens(s.platform, campaign);
  return required.length > 0 ? { source: 'required', tokens: required } : null;
}

/**
 * The automatic rule check only speaks while the clip waits for a human
 * verdict. Once an auditor approved or disapproved it, a leftover rule failure
 * would contradict (or add noise to) that verdict — on 2026-09-30 all 313
 * failing checks were on approved clips, most of them TikTok mentions the
 * worker could not see.
 */
function awaitingHumanVerdict(s: SubmissionFeedbackInput): boolean {
  return s.admin_approval !== 'approved' && s.admin_approval !== 'disapproved';
}

/**
 * A raw Strapi content-manager edit (no admin page enforces a reason there)
 * leaves `disqualification_reason` empty — `kind: 'disqualified'` with
 * `reasonCode: null` covers that case so the clipper never sees a bare dash
 * for something that affects their earnings.
 *
 * `adminResolveSuspectedDuplicate`'s DISQUALIFY action stores the internal
 * `duplicate_of:<submissionId>` marker as the reason — normalize it to a
 * stable code so it renders as an explanatory sentence instead of a raw id.
 *
 * `rules_check_failures` comes from the automatic pre-approval gate (stable
 * codes like `missing_hashtag`, `missing_mention`, `platform_not_allowed`).
 * It only surfaces when nothing more authoritative — a real disqualification,
 * an admin approval/disapproval reason, or a suspected-duplicate flag — is
 * already explaining the row, and only while the clip still waits for a
 * human verdict (`awaitingHumanVerdict`), so a human verdict is never
 * overshadowed or contradicted by what the automatic gate found.
 */
export function getSubmissionFeedback(
  s: SubmissionFeedbackInput,
  campaign?: SubmissionFeedbackCampaign,
): SubmissionFeedback {
  if (s.disqualified) {
    const raw = s.disqualification_reason?.trim() || null;
    const reasonCode = raw?.startsWith('duplicate_of:') ? 'suspected_duplicate_resolved' : raw;
    return { kind: 'disqualified', reasonCode };
  }
  if (s.approval_reason) {
    const muted = s.admin_approval === 'approved';
    const captionTokens = !muted && CAPTION_CODES.has(s.approval_reason) ? captionTokensFor(s, campaign) : null;
    return { kind: 'approval', reasonCode: s.approval_reason, muted, ...(captionTokens && { captionTokens }) };
  }
  if (s.suspected_duplicate) {
    return { kind: 'underReview' };
  }
  const ruleCodes = awaitingHumanVerdict(s) ? (s.rules_check_failures ?? []) : [];
  if (ruleCodes.length > 0) {
    const captionTokens = ruleCodes.some(code => CAPTION_CODES.has(code)) ? captionTokensFor(s, campaign) : null;
    return { kind: 'rulesCheckFailures', codes: ruleCodes, ...(captionTokens && { captionTokens }) };
  }
  return { kind: 'none' };
}
